/**
 * What a notification does on a device type (I-2915) — the client's copy of
 * master's rule (`backend/master/notifications/preferences.go`:
 * `Preferences.SettingFor`, `DefaultSetting`); keep both in step.
 *
 * The settings are fields of the shell's key, per device type
 * (`browser` „Web", `desktop` „Desktop", `mobile` „Mobil"):
 *
 *   notifications.devices.<device type>.modules.<module key>               off | bell | system
 *   notifications.devices.<device type>.types.<module key>.<type name>     off | bell | system
 *
 * `off`: the notification does not appear there; `bell`: an entry in the
 * bell; `system`: the bell and a system notification (a banner of the
 * browser or the operating system, on a phone a push). A type without a
 * setting follows its module — except a type that is off by default
 * (`enabledByDefault: false`), which stays off until it is set; a module
 * without one follows `defaultNotificationSetting`. `notifications.enabled`
 * false turns everything off.
 */

/** The device types the settings distinguish, in the order the settings list shows them. */
export const NOTIFICATION_DEVICE_TYPES = ['browser', 'desktop', 'mobile'] as const;
export type NotificationDeviceType = (typeof NOTIFICATION_DEVICE_TYPES)[number];

/** What a notification does on one device type. */
export const NOTIFICATION_DEVICE_SETTINGS = ['off', 'bell', 'system'] as const;
export type NotificationDeviceSetting = (typeof NOTIFICATION_DEVICE_SETTINGS)[number];

/** The device type of the Connect web shell. */
export const SHELL_DEVICE_TYPE: NotificationDeviceType = 'browser';

/** The key master counts its own notifications under. */
const MASTER_MODULE = 'master';

/**
 * The modules whose notifications are system notifications in a browser
 * while no layer says otherwise: a calendar reminder is due now, so it pops
 * up even when the bell is out of sight.
 */
const SYSTEM_IN_BROWSER = new Set(['calendar']);

function isSetting(value: unknown): value is NotificationDeviceSetting {
  return (
    typeof value === 'string' && (NOTIFICATION_DEVICE_SETTINGS as readonly string[]).includes(value)
  );
}

function objectAt(value: unknown, key: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const next = (value as Record<string, unknown>)[key];
  return next && typeof next === 'object' && !Array.isArray(next)
    ? (next as Record<string, unknown>)
    : {};
}

/**
 * What the notifications of a module do on a device type while no layer
 * sets it: a system notification on desktops and phones; in a browser the
 * bell, and a system notification for the calendar.
 */
export function defaultNotificationSetting(
  device: NotificationDeviceType,
  moduleKey: string
): NotificationDeviceSetting {
  return device === 'browser' && !SYSTEM_IN_BROWSER.has(moduleKey) ? 'bell' : 'system';
}

/**
 * What a notification of type `typeKey` (`<module>.<name>`), produced by
 * `moduleKey` (master's own: `master` or empty), does on `device` by the
 * effective `shell` document `shell`. `enabledByDefault` is the type's own
 * default from master's register; a type the register does not know counts
 * as on.
 */
export function notificationDeviceSettingFor(
  shell: unknown,
  device: NotificationDeviceType,
  moduleKey: string,
  typeKey: string,
  enabledByDefault = true
): NotificationDeviceSetting {
  const notifications = objectAt(shell, 'notifications');
  if (notifications.enabled === false) return 'off';
  const settings = objectAt(objectAt(notifications, 'devices'), device);
  const dot = typeKey.indexOf('.');
  if (dot > 0) {
    const own = objectAt(objectAt(settings, 'types'), typeKey.slice(0, dot))[
      typeKey.slice(dot + 1)
    ];
    if (isSetting(own)) return own;
  }
  if (!enabledByDefault) return 'off';
  const module = moduleKey || MASTER_MODULE;
  const moduleSetting = objectAt(settings, 'modules')[module];
  if (isSetting(moduleSetting)) return moduleSetting;
  return defaultNotificationSetting(device, module);
}
